import { Modal } from 'bootstrap';
import htmx from 'htmx.org';

type Provider = { id: string; name: string; role: string };
type Preview = { patientName: string; document: string; practiceName: string; groupName?: string;
  currentProfessionalId?: string; canAssign: boolean; issue?: string; providers: Provider[] };
type Selection = { ticket: string; proposedProfessionalId: string; reason: string };

const dialog = document.getElementById('practice-provider-dialog');
const form = dialog?.querySelector<HTMLFormElement>('[data-provider-form]');
const choice = dialog?.querySelector<HTMLSelectElement>('[data-provider-choice]');
const reason = dialog?.querySelector<HTMLTextAreaElement>('[data-provider-reason]');
const submit = dialog?.querySelector<HTMLButtonElement>('[data-provider-submit]');
const refresh = dialog?.querySelector<HTMLButtonElement>('[data-provider-refresh]');
const context = dialog?.querySelector<HTMLElement>('[data-provider-context]');
const feedback = dialog?.querySelector<HTMLElement>('[data-provider-feedback]');
let trigger: HTMLElement | null = null;
let pending: AbortController | null = null;
let ticket: string | null = null;
let selection: Selection | null = null;
let saving = false;
let completed = false;

function message(value: string, error = false) {
  if (!feedback) return;
  feedback.textContent = value;
  feedback.className = `alert ${error ? 'alert-warning' : 'alert-info'} mb-0`;
}

async function preview(source: HTMLElement) {
  if (!dialog || !choice || !reason || !submit || !refresh || !context) return;
  const url = new URL(source.dataset.practiceProvider ?? '', window.location.href);
  if (url.origin !== window.location.origin || !url.pathname.startsWith('/atenciones/prestadores/')) return;
  pending?.abort();
  const controller = new AbortController();
  pending = controller;
  trigger = source;
  ticket = null; selection = null; completed = false;
  choice.replaceChildren(new Option('Cargando equipo…', ''));
  choice.disabled = true; reason.disabled = true; reason.value = ''; submit.disabled = true;
  submit.textContent = 'Confirmar cambio'; refresh.hidden = true;
  context.textContent = '';
  message('Cargando la práctica y el equipo de la sede…');
  Modal.getOrCreateInstance(dialog).show();
  try {
    const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    const result = await response.json().catch(() => ({ message: 'No se pudo consultar la práctica. Revisá tu sesión e intentá nuevamente.' }));
    if (pending !== controller) return;
    if (!response.ok) throw new Error(result.message ?? 'No se pudo consultar la práctica.');
    if (!result.preview || typeof result.preview !== 'object')
      throw new Error('No se pudo consultar la práctica. Revisá tu sesión e intentá nuevamente.');
    const item = result.preview as Preview;
    context.textContent = `${item.patientName} · ${item.document ? 'DNI ' + item.document : 'Sin DNI registrado'} · ${item.practiceName}`;
    choice.replaceChildren(new Option('Elegí un prestador del equipo', ''));
    for (const provider of item.providers ?? [])
      choice.add(new Option(provider.role ? `${provider.name} · ${provider.role}` : provider.name, provider.id));
    choice.value = item.currentProfessionalId ?? '';
    ticket = result.ticket ?? null;
    choice.disabled = reason.disabled = !item.canAssign || !ticket;
    submit.disabled = !item.canAssign || !ticket;
    message(item.canAssign && ticket ? [item.issue, 'Confirmá el prestador y completá el motivo del cambio.'].filter(Boolean).join(' ')
      : item.issue ?? 'Esta práctica no admite cambios de prestador.');
    if (ticket) choice.focus();
  } catch (error) {
    if (controller.signal.aborted) return;
    message(error instanceof Error && error.message !== 'Failed to fetch' ? error.message : 'No se pudo conectar. Reintentá consultar la práctica.', true);
    refresh.hidden = false;
  }
}

function hasPendingInputs(container: Element) {
  return Array.from(container.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('input:not([type=hidden]),textarea,select'))
    .some(input => input instanceof HTMLSelectElement
      ? Array.from(input.options).some(option => option.selected !== option.defaultSelected)
      : input instanceof HTMLInputElement && ['checkbox', 'radio'].includes(input.type)
        ? input.checked !== input.defaultChecked : input.value !== input.defaultValue);
}

async function refreshContext(source: HTMLElement) {
  const container = source.closest<HTMLElement>('[data-provider-list]');
  if (!container || !container.dataset.providerRefreshUrl || hasPendingInputs(container)) return false;
  const response = await fetch(container.dataset.providerRefreshUrl, { credentials: 'same-origin' });
  if (!response.ok) return false;
  const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
  const selector = container.dataset.providerRefreshSelector;
  const replacement = selector ? parsed.querySelector(selector) : parsed.querySelector('[data-provider-list]');
  if (!replacement) return false;
  container.replaceChildren(...Array.from(replacement.childNodes));
  htmx.process(container);
  document.dispatchEvent(new CustomEvent('htmx:afterSwap', { detail: { elt: container } }));
  return true;
}

async function confirm(event: Event) {
  event.preventDefault();
  if (saving || completed || !form || !dialog || !choice || !reason || !submit || !refresh || !ticket || !trigger) return;
  if (!form.reportValidity()) return;
  selection ??= { ticket, proposedProfessionalId: choice.value, reason: reason.value.trim() };
  saving = true;
  choice.disabled = reason.disabled = submit.disabled = true;
  dialog.setAttribute('aria-busy', 'true');
  message('Guardando la asignación…');
  try {
    const response = await fetch(form.dataset.confirmUrl!, { method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', RequestVerificationToken: form.querySelector<HTMLInputElement>('[name="__RequestVerificationToken"]')?.value ?? '' },
      body: JSON.stringify(selection) });
    const result = await response.json();
    if (!response.ok) {
      message(result.message ?? 'No se pudo confirmar el cambio.', true);
      if (response.status >= 500) { submit.disabled = false; submit.textContent = 'Reintentar confirmación'; }
      else { ticket = null; refresh.hidden = false; }
      return;
    }
    completed = true;
    // Refresh only the practice list; keep filters, selected patient and other forms intact.
    let refreshed = false;
    try { refreshed = await refreshContext(trigger); } catch { /* Assignment succeeded; never replay it to refresh the view. */ }
    document.dispatchEvent(new CustomEvent('practice-provider:changed', { detail: result.preview }));
    if (refreshed) {
      Modal.getOrCreateInstance(dialog).hide();
      window.profintUI?.toast?.success('Prestador actualizado en esta práctica.');
    } else message('Prestador actualizado. Conservamos tus datos pendientes; actualizá las prácticas después de guardarlos.');
  } catch {
    message('No se pudo verificar el resultado. Reintentá la confirmación con los mismos datos.', true);
    submit.disabled = false; submit.textContent = 'Reintentar confirmación';
  } finally { saving = false; dialog.removeAttribute('aria-busy'); }
}

document.addEventListener('click', event => {
  const source = (event.target as Element | null)?.closest<HTMLElement>('[data-practice-provider]');
  if (!source || saving) return;
  event.preventDefault();
  void preview(source);
});
form?.addEventListener('submit', confirm);
refresh?.addEventListener('click', () => { if (trigger && !saving) void preview(trigger); });
dialog?.addEventListener('hide.bs.modal', event => { if (saving) event.preventDefault(); });
dialog?.addEventListener('hidden.bs.modal', () => { pending?.abort(); trigger?.focus({ preventScroll: true }); });
